import React from "react";
import { Box, Typography } from "@mui/material";
import { MULISH } from "../../WireGuardTunnels/VpnDebugger/vpnDebugConstants";
import { useTroubleshootingStore } from "../troubleshootingStore/troubleshootingStore";
import {
  AlarmReceiver,
  AlertsReceived,
  GapResult,
} from "../troubleshootingInterfaces";
import {
  CameraLink,
  Card,
  CardHeader,
  SiteLink,
  GapNotice,
  Hint,
  Pill,
  SampleChip,
  TS_COLORS,
  linkifySite,
} from "../TroubleshootingUi";
import { useScopeLabel } from "../TroubleshootingUtils/useScope";
import { useScopeKinds } from "../TroubleshootingUtils/useScopeKinds";
import { getArmedStatusPresentation } from "../../Sites/SitesPage/SitesPageUtils/armedStatusPresentation";

const clock = (iso: string) =>
  new Date(iso).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });

/**
 * The one sentence a customer asking "am I receiving alarms?" needs first:
 * whether alarms are flowing right now, for exactly the scope they picked.
 */
export const alertsReceivedSentence = (
  alerts: AlertsReceived,
  scopeLabel: string
): string => {
  const who = scopeLabel === "All sites" ? "your account" : scopeLabel;
  // The time is the window's start, said as such: "since 4:53 PM" on its own
  // read as the moment the last alert arrived.
  if (alerts.count === 0)
    return `No alerts processed for ${who} in the last ${
      alerts.windowHours === 1 ? "hour" : `${alerts.windowHours} hours`
    } (since ${clock(alerts.since)}).`;
  const latest = alerts.latest
    ? `, latest ${clock(alerts.latest.at)} from ${alerts.latest.siteName}${
        alerts.latest.cameraName ? ` · ${alerts.latest.cameraName}` : ""
      }`
    : "";
  const n = alerts.count.toLocaleString();
  return `We processed ${alerts.truncated ? `at least ${n}` : n} alert${
    alerts.count === 1 ? "" : "s"
  } for ${who} in the last ${
    alerts.windowHours === 1 ? "hour" : `${alerts.windowHours} hours`
  }${latest}.`;
};

/**
 * "Accepted by N receivers" — only from real receiver health (ENG-1523's
 * rollup, `last_accepted_at`). Omitted entirely while no receiver has one.
 * "Accepted", not "delivered": an SMTP 250 says the receiver took the alarm,
 * not that an operator saw it.
 */
export const deliveredSentence = (
  receivers: GapResult<AlarmReceiver[]> | null,
  sinceIso: string
): string | null => {
  if (receivers?.status !== "ok") return null;
  const since = new Date(sinceIso).getTime();
  const recent = receivers.data.filter(
    (r) =>
      r.health.last_accepted_at &&
      new Date(r.health.last_accepted_at).getTime() >= since
  );
  if (recent.length === 0) return null;
  const last = recent
    .map((r) => r.health.last_accepted_at as string)
    .sort()
    .reverse()[0];
  return `Accepted by ${recent.length} receiver${
    recent.length === 1 ? "" : "s"
  }, last at ${clock(last)}.`;
};

export const AlertsReceivedCard = () => {
  const { alertsReceived, receivers, groupId, scope, siteId, siteDetail } =
    useTroubleshootingStore();
  const scopeLabel = useScopeLabel();
  const siteInScope = scope === "site" ? siteId : null;
  const kinds = useScopeKinds();
  // Name only the links this scope has; "unknown yet" still counts, so the
  // list never drops a link that turns out to be there.
  const links = [
    kinds.paths !== false ? "the VPN" : "",
    kinds.wireguard !== false ? "the WireGuard peer" : "",
    kinds.smtp !== false ? "SMTP delivery" : "",
    "the alarm receiver",
    kinds.wireguard === false && kinds.smtp === false && kinds.paths === false
      ? "the cameras themselves"
      : "",
  ].filter(Boolean);
  const linkList =
    links.length > 1
      ? `${links.slice(0, -1).join(", ")} or ${links[links.length - 1]}`
      : links[0];
  if (groupId == null) return null;

  // One site that is not armed (inactive, disarmed, or still arming or
  // disarming) generates no alarms by design, so a zero count is expected, not
  // a fault. Same presentation the findings and the site page use. Unknown —
  // group scope, or the detail not loaded — keeps the fault framing.
  const arming =
    scope === "site" &&
    siteDetail != null &&
    siteDetail.id === siteId &&
    (siteDetail.armed != null || siteDetail.armed_status)
      ? getArmedStatusPresentation(
          siteDetail.armed_status,
          siteDetail.armed,
          siteDetail.active ?? true
        )
      : null;
  const notArmed =
    arming != null && (arming.tone === "disarmed" || arming.transitional);
  const notArmedReason = !notArmed
    ? ""
    : siteDetail?.active === false
    ? "is inactive"
    : arming?.status === "DISARMING"
    ? "is disarming"
    : arming?.transitional
    ? "is still arming"
    : "is disarmed";

  if (!alertsReceived) {
    return (
      <Card testId="alerts-received-card">
        <CardHeader
          title="Alerts received"
          hint="What the monitoring API processed for this scope in the last hour."
        />
        <Box sx={{ px: 2.5, pt: 2, pb: 2.25 }}>
          <Hint>Counting recent alerts…</Hint>
        </Box>
      </Card>
    );
  }
  if (alertsReceived.status !== "ok") {
    return (
      <Card testId="alerts-received-card">
        <CardHeader
          title="Alerts received"
          hint="What the monitoring API processed for this scope in the last hour."
        />
        <GapNotice result={alertsReceived} what="Recent alert counts" />
      </Card>
    );
  }

  const alerts = alertsReceived.data;
  const zero = alerts.count === 0;
  const expectedZero = zero && notArmed;
  const delivered = deliveredSentence(receivers, alerts.since);
  return (
    <Card
      testId="alerts-received-card"
      accent={zero && !expectedZero ? TS_COLORS.amber : undefined}
    >
      <CardHeader
        title="Alerts received"
        hint="What the monitoring API processed for this scope in the last hour."
        right={
          <>
            {alertsReceived.sample && <SampleChip />}
            <Pill tone={expectedZero ? "grey" : zero ? "warn" : "ok"}>
              {expectedZero
                ? "None — expected"
                : zero
                ? "None in the last hour"
                : `${alerts.count.toLocaleString()}${
                    alerts.truncated ? "+" : ""
                  } in the last hour`}
            </Pill>
          </>
        }
      />
      <Box sx={{ px: 2.5, pt: 2, pb: 2.25 }}>
        <Typography
          sx={{ ...MULISH, fontSize: 15, lineHeight: 1.5, maxWidth: "90ch" }}
          data-testid="alerts-received-sentence"
        >
          {alerts.latest && alerts.latest.sitePk != null ? (
            <>
              {alertsReceivedSentence(
                { ...alerts, latest: null },
                scopeLabel
              ).replace(/\.$/, "")}
              , latest {clock(alerts.latest.at)} from{" "}
              <SiteLink siteId={alerts.latest.sitePk}>
                {alerts.latest.siteName}
              </SiteLink>
              {alerts.latest.cameraName ? (
                <>
                  {" · "}
                  {alerts.latest.cameraPk != null ? (
                    <CameraLink
                      siteId={alerts.latest.sitePk}
                      cameraId={alerts.latest.cameraPk}
                    >
                      {alerts.latest.cameraName}
                    </CameraLink>
                  ) : (
                    alerts.latest.cameraName
                  )}
                </>
              ) : null}
              .
            </>
          ) : (
            linkifySite(
              alertsReceivedSentence(alerts, scopeLabel),
              siteInScope,
              scopeLabel
            )
          )}
          {delivered ? ` ${delivered}` : ""}
        </Typography>
        {expectedZero ? (
          <Box sx={{ mt: 1 }} data-testid="alerts-received-expected">
            <Hint>
              Expected: {linkifySite(scopeLabel, siteInScope, scopeLabel)}{" "}
              {notArmedReason}, so it does not generate alarms. Nothing is wrong
              with its connection or cameras.
            </Hint>
          </Box>
        ) : zero ? (
          <Box sx={{ mt: 1 }}>
            <Hint>
              Nothing has reached us for this scope in the window. Start from
              &quot;Not receiving any alarms&quot; below: it walks the chain one
              link at a time — {linkList}.
              {/* The advice only applies to a site that is armed; for a group
                  the arm state of each site is not known here. */}{" "}
              <b>Do not disarm and re-arm the site</b>: it does not help, and it
              hides the evidence that shows where the alarm stopped.
            </Hint>
          </Box>
        ) : (
          alerts.camerasAlerted > 0 && (
            <Box sx={{ mt: 1 }}>
              <Hint>
                {alerts.camerasAlerted} camera
                {alerts.camerasAlerted === 1 ? "" : "s"} alerted in the window.
              </Hint>
            </Box>
          )
        )}
      </Box>
    </Card>
  );
};
